import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  activeFiltersCount,
  createShoppingObject,
  createShoppingPriceObject,
  loadAccessoryImagePlaceholder,
  stringOnlyHTML
} from '@models/functions.model';
import { CartInfo } from '@models/session-details';
import {
  ShoppingCartData,
  ShoppingCartItem,
  ShoppingCartPriceItem
} from '@models/shopping-cart.model';
import {
  ACCESSORY_LIST_SORT_VALUES,
  AccessoriesData,
  AccessoryItemData,
  CART_ITEM_TYPE_ACCESSORY,
  CATEGORY_FILTER_SORT_CLASSES,
  FilterToken,
  ShoppingCategoryData,
  ShoppingCategoryItem,
  ShoppingFilterData,
  ShoppingSortData
} from '@models/shopping.model';
import { NgbDropdownModule, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { AssetCategoryFilterComponent } from '@shared-components/asset-filters/asset-category-filter/asset-category-filter.component';
import { FilterTokensComponent } from '@shared-components/asset-filters/filter-tokens/filter-tokens.component';
import { FiltersIndexComponent } from '@shared-components/asset-filters/filters-index/filters-index.component';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Subject, Subscription, distinctUntilChanged } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  DataLoaderService
} from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../admin-override/admin-override-badge/admin-override-badge.component';
import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';

const INCREMENTER_BTN_CLASSES: string =
  'btn btn-outline-secondary d-flex justify-content-center px-4 accessory-card__incrementer--btn';

@Component({
  selector: 'ts-accessories',
  templateUrl: './accessories.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    NgbDropdownModule,
    NgbTooltipModule,
    TranslationPipe,
    DataLoaderDirective,
    AdminOverrideBannerComponent,
    FiltersIndexComponent,
    AssetCategoryFilterComponent,
    FilterTokensComponent,
    AdminOverrideBadgeComponent,
    PriceSummaryComponent
  ]
})
export class AccessoriesComponent implements OnInit, OnDestroy {
  order_type_id: number;
  device_id: number;
  line_id: number;
  accessories: AccessoryItemData[] = [];
  accessory_cart_items: ShoppingCartItem[] = [];
  accessory_detail_modal_body: string = '';
  accessory_detail_modal_title: string = '';
  categories: ShoppingCategoryData[];
  current_sort: ShoppingSortData;
  filter_tokens: FilterToken[];
  filters: ShoppingFilterData[];
  filters_count: number = 0;
  selected_sort_dropdown_value: string;
  show_no_results: boolean = false;
  cart_updated: boolean | undefined;
  admin_toggle_hide_alert: boolean = false;
  sort_options: string[] = ACCESSORY_LIST_SORT_VALUES;
  incrementer_classes: string = INCREMENTER_BTN_CLASSES;
  category_filter_sort_classes: string = CATEGORY_FILTER_SORT_CLASSES;
  loader_key: string | undefined = 'global';

  // Price Summary Component Inputs
  skip_button: boolean = true;
  button_status: boolean;
  refresh_summary: Subject<boolean | string> = new Subject<boolean | string>();
  shopping_cart: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_state_svc: ActivityStateService,
    private data_loader_svc: DataLoaderService,
    private filter_state_svc: FilterStateService,
    private modal_svc: ModalService,
    private session_svc: SessionService,
    private route: ActivatedRoute,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {
    this.order_type_id = this.activity_state_svc.orderTypeId()!;
    this.sort_options = this.translation_svc.getTranslatedAccessorySortValues();
  }

  ngOnInit(): void {
    this.shopping_cart.orderTypeId = this.shopping_items_price.orderTypeId =
      this.activity_state_svc.orderTypeId()!;
    this.shopping_cart.lineId = this.shopping_items_price.lineId =
      this.activity_state_svc.parameter('lineId');
    this.button_status = [14].includes(this.order_type_id) ? true : false;

    this.shopping_cart.accessories_include_admin_options = this.activity_state_svc.parameter(
      'accessories_include_admin_options'
    );

    this.route.queryParamMap.subscribe({
      next: (paramMap: ParamMap) => {
        this.device_id = Number(paramMap.get('deviceId'));
        this.line_id = Number(paramMap.get('lineId'));
        this.shopping_cart.keep_plan = this.shopping_items_price.keep_plan =
          paramMap.get('keep_plan') === 'true';

        this.setDefaultSort();

        this.filter_state_svc.filtersParam = {
          categories:
            paramMap.get('ct')?.split('&')[0] && paramMap.get('ct')?.split('&')[1]
              ? [
                  {
                    id: Number(paramMap.get('ct')?.split('&')[0]),
                    value: paramMap.get('ct')?.split('&')[1]!
                  }
                ]
              : [],
          filters: [],
          orderTypeId: this.activity_state_svc.orderTypeId()!,
          deviceId: this.device_id !== 0 ? this.device_id : null,
          refDeviceId:
            [14].includes(this.order_type_id) && this.device_id !== 0 ? this.device_id : undefined,
          lineId: [14].includes(this.order_type_id) && this.line_id ? this.line_id : undefined,
          include_admin_options: this.admin_override
        };

        if (this.filter_state_svc.filtersParam.categories.length === 0) {
          this.subscriptions.add(
            this.shopping_svc.getShoppingItems().subscribe({
              next: (res: ShoppingCartData) => {
                if (res.shopping_cart_items.length > 0 || [14].includes(this.order_type_id)) {
                  let accessories: ShoppingCartItem[] = [];
                  res.shopping_cart_items.forEach((cart_item: ShoppingCartItem) => {
                    if (cart_item.type === CART_ITEM_TYPE_ACCESSORY) accessories.push(cart_item);
                  })!;
                  if (accessories.length > 0) {
                    this.filter_state_svc.filtersParam.categories = [
                      {
                        value: accessories[0]?.additional_info?.category!,
                        id: accessories[0].additional_info?.category_id!
                      }
                    ];
                  }
                  this.subscriptions.add(
                    this.filter_state_svc.filter_changes.pipe(distinctUntilChanged()).subscribe({
                      next: () =>
                        this.getAccessories(this.current_sort.field, this.current_sort.dir)
                    })
                  );
                }
              }
            })
          );
        } else {
          this.subscriptions.add(
            this.filter_state_svc.filter_changes.pipe(distinctUntilChanged()).subscribe({
              next: () => this.getAccessories(this.current_sort.field, this.current_sort.dir)
            })
          );
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.filter_state_svc.filtersParam.categories = [];
    this.filter_state_svc.filtersParam.filters = [];
  }

  get category_name(): string {
    return (
      this.categories &&
      this.categories[0].items.filter((category: ShoppingCategoryItem) => category.active)[0]
        .display_name
    );
  }

  get navigate_on_continue(): string {
    return `shop/${this.order_type_id}/finalize-order`;
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get disable_continue_btn(): boolean {
    if ([14].includes(this.order_type_id))
      return this.shopping_cart.shopping_cart_items.length > 0 ? false : true;
    else
      return (
        this.data_loader_svc.isLoading('accessories-list') ||
        this.data_loader_svc.isLoading('shopping-cart-items')
      );
  }

  get shopping_cart_accessory(): ShoppingCartItem[] {
    return this.shopping_cart.shopping_cart_items.filter(
      (i: ShoppingCartItem) => i.type === CART_ITEM_TYPE_ACCESSORY
    );
  }

  get admin_cart_items(): ShoppingCartItem[] | undefined {
    return CartInfo.cart_data?.shopping_cart_items.filter(
      (item: ShoppingCartItem) =>
        CART_ITEM_TYPE_ACCESSORY === item.type && item.additional_info?.admin
    )!;
  }

  get admin_override(): boolean | undefined {
    return this.shopping_cart.accessories_include_admin_options ?? false;
  }

  sanitizedHTML = (description: string | undefined): boolean =>
    !stringOnlyHTML(description) && description !== '';

  generateAccessoryImage = (event: any, accessory: AccessoryItemData): string =>
    loadAccessoryImagePlaceholder(accessory.category, event);

  confirmCartChange(admin_override: boolean): void {
    if (this.admin_cart_items?.length && !admin_override)
      this.modal_svc.confirmationDialog(
        {
          title: this.translation_svc.getTranslatedModalLabels()[0],
          message: this.translation_svc.getTranslatedModalLabels()[1],
          confirm_button_text: this.translation_svc.translate('continue'),
          cancel_button_text: this.translation_svc.translate('cancel'),
          onConfirm: () => {
            this.admin_toggle_hide_alert = true;
            this.shopping_svc
              .clearSpecifiedCartItems(
                [...(this.admin_cart_items?.map((item: ShoppingCartItem) => item.id) as number[])],
                false
              )
              .subscribe((res: ShoppingCartData) => {
                this.shopping_cart.shopping_cart_items =
                  this.shopping_items_price.shopping_cart_items = res.shopping_cart_items;

                this.getAdminOverride(admin_override);
              });
          }
        },
        {
          size: 'dialog-centered',
          centered: true,
          backdrop: 'static'
        }
      );
  }

  getAdminOverride(admin_override: boolean): void {
    this.setDefaultSort();
    this.filter_state_svc.filtersParam = {
      ...this.filter_state_svc.filtersParam,
      filter_tokens: [],
      categories: [],
      filters: [],
      include_admin_options: admin_override
    };
    this.shopping_cart.accessories_include_admin_options = admin_override;
  }

  getShoppingCartData(cart_data: ShoppingCartData): void {
    if (cart_data) {
      this.shopping_cart = {
        ...cart_data,
        accessories_include_admin_options: this.admin_override
      };

      let asset_changed: boolean = false;
      this.shopping_cart.shopping_cart_items.forEach((item: ShoppingCartItem) => {
        if (item.type === CART_ITEM_TYPE_ACCESSORY) {
          if (this.device_id === 0 && this.line_id !== 0) {
            asset_changed = true;
            this.filter_state_svc.filtersParam.deviceId = null;
            this.filter_state_svc.filtersParam.lineId = this.line_id;
            item.edit_link_payload = JSON.stringify(this.filter_state_svc.filtersParam);
          } else if (this.line_id === 0 && this.device_id !== 0) {
            asset_changed = true;
            this.filter_state_svc.filtersParam.lineId = null;
            this.filter_state_svc.filtersParam.deviceId = this.device_id;
            item.edit_link_payload = JSON.stringify(this.filter_state_svc.filtersParam);
          }
        }
      });

      if (asset_changed)
        setTimeout(() => {
          this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe({
            next: (res: ShoppingCartData) => {
              this.shopping_cart = res;
            },
            error: (err: HttpErrorResponse) => {}
          });
        }, 2000);

      if (
        this.cart_updated &&
        this.shopping_cart_accessory?.length === 0 &&
        this.accessories.length > 0
      )
        this.skip_button = true;

      if (this.shopping_cart_accessory?.length > 0) this.button_status = false;
    }
  }

  getAccessories(field: string, dir: string): void {
    this.shopping_svc
      .getAccessories(this.filter_state_svc.filtersParam, this.loader_key)
      .subscribe({
        next: (res: AccessoriesData) => {
          this.loader_key = undefined;
          res.accessories.map(
            (val: AccessoryItemData) => (val.category_id = res.category[0].config.filter_config_id)
          );
          this.accessories = this.sortAccessories(res.accessories, field, dir);
          this.show_no_results = !(this.accessories.length > 0);
          this.cart_updated = res.cart_updated;

          this.categories = res.category;
          this.updateCategoryWithAll(this.categories);

          this.filters = res.filters.filter((f) => f.items.length > 0);
          this.filter_tokens = res.filter_tokens;
          this.filters_count = activeFiltersCount(this.filters);

          this.skip_button = this.accessories.length > 0 ? true : false;

          this.activity_state_svc.setSteps(res.progress_bar_config.steps);

          this.retrieveCartAccessories(this.shopping_cart);
        },
        error: (err: HttpErrorResponse) => {}
      });
  }

  constructAccessoryTitle(accessory: AccessoryItemData): string {
    let title: string = '';
    title +=
      accessory.manufacturer && !['Unknown', ''].includes(accessory.manufacturer)
        ? accessory.manufacturer + ' - '
        : '';
    title += accessory.name;
    title += accessory.color ? ' - ' + accessory.color : '';

    return title;
  }

  sortAccessoryList(sort_label: string): void {
    switch (sort_label) {
      case this.sort_options[0]: {
        this.current_sort = {
          field: 'price',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[0];
        this.accessories = this.sortAccessories(this.accessories, 'price', 'asc');
        break;
      }
      case this.sort_options[1]: {
        this.current_sort = {
          field: 'price',
          dir: 'desc'
        };
        this.selected_sort_dropdown_value = this.sort_options[1];
        this.accessories = this.sortAccessories(this.accessories, 'price', 'desc');
        break;
      }
      case this.sort_options[2]: {
        this.current_sort = {
          field: 'name',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[2];
        this.accessories = this.sortAccessories(this.accessories, 'name', 'asc');
        break;
      }
      case this.sort_options[3]: {
        this.current_sort = {
          field: 'name',
          dir: 'desc'
        };
        this.selected_sort_dropdown_value = this.sort_options[3];
        this.accessories = this.sortAccessories(this.accessories, 'name', 'desc');
        break;
      }
      default: {
        this.current_sort = {
          field: 'price',
          dir: 'asc'
        };
        this.selected_sort_dropdown_value = this.sort_options[0];
        this.accessories = this.sortAccessories(this.accessories, 'price', 'asc');
        break;
      }
    }

    this.pinCartAccessories();
  }

  openMobileFilters(content: TemplateRef<any>): void {
    this.modal_svc.open(content, { fullscreen: true });
  }

  openDetailsModal(accessory: AccessoryItemData): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = this.constructAccessoryTitle(accessory);
    modalRef.componentInstance.modal_body = accessory.description;
    modalRef.componentInstance.limit_modal_title = false;
    modalRef.componentInstance.normalize_external_styles = true;
  }

  addToCart(accessory: AccessoryItemData, append_accessory: boolean = false): void {
    if (append_accessory) accessory.quantity!++;
    else {
      accessory.in_cart = true;
      accessory.quantity = 1;
    }

    let shopping_item_object: ShoppingCartItem = createShoppingObject(
      accessory,
      CART_ITEM_TYPE_ACCESSORY
    );
    shopping_item_object.edit_link_payload = JSON.stringify(this.filter_state_svc.filtersParam);

    let shopping_object_to_send: ShoppingCartData = {
      ...this.shopping_cart,
      accessories_include_admin_options: this.admin_override,
      shopping_cart_items: [shopping_item_object],
      orderTypeId: this.order_type_id,
      lineId: this.activity_state_svc.parameter('lineId')
    };

    let price_object: ShoppingCartPriceItem = createShoppingPriceObject(
      accessory,
      CART_ITEM_TYPE_ACCESSORY
    );

    if (append_accessory) {
      this.shopping_items_price.shopping_cart_items.forEach((item: ShoppingCartItem) => {
        if (item.id === price_object.id) {
          item.quantity!++;
        }
      });

      this.shopping_svc.updateShoppingItems(shopping_object_to_send, true).subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart.shopping_cart_items = this.shopping_items_price.shopping_cart_items =
            res.shopping_cart_items;

          if (
            [14].includes(this.order_type_id) &&
            this.shopping_cart.shopping_cart_items.length > 0
          )
            this.button_status = false;

          this.refresh_summary.next(true);
        },
        error: (err) => {}
      });
    } else {
      this.shopping_items_price.shopping_cart_items.push(price_object);
      this.shopping_svc.sendShoppingItems(shopping_object_to_send).subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart.shopping_cart_items = res.shopping_cart_items;
          this.skip_button = this.shopping_cart_accessory?.length > 0 ? false : true;
          if (
            [14].includes(this.order_type_id) &&
            this.shopping_cart.shopping_cart_items.length > 0
          )
            this.button_status = false;

          this.refresh_summary.next(true);
        },
        error: (err: HttpErrorResponse) => {}
      });
    }
  }

  removeFromCart(accessory: AccessoryItemData): void {
    let shopping_item_object: ShoppingCartItem = createShoppingObject(
      accessory,
      CART_ITEM_TYPE_ACCESSORY
    );
    let shopping_object_to_send: ShoppingCartData = {
      ...this.shopping_cart,
      shopping_cart_items: [shopping_item_object],
      accessories_include_admin_options: this.admin_override,
      orderTypeId: this.order_type_id,
      lineId: this.activity_state_svc.parameter('lineId')
    };

    shopping_item_object.edit_link_payload = JSON.stringify(this.filter_state_svc.filtersParam);
    accessory.quantity!--;
    accessory.in_cart = accessory.quantity === 0 ? false : true;

    this.shopping_svc.updateShoppingItems(shopping_object_to_send, false).subscribe({
      next: (res: ShoppingCartData) => {
        this.shopping_cart.shopping_cart_items = this.shopping_items_price.shopping_cart_items =
          res.shopping_cart_items;
        if (
          [14].includes(this.order_type_id) &&
          this.shopping_cart.shopping_cart_items.length === 0
        )
          this.button_status = true;

        if (this.shopping_cart.shopping_cart_items.length > 0)
          this.skip_button = this.shopping_cart_accessory?.length > 0 ? false : true;

        this.refresh_summary.next(true);
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private sortAccessories(
    accessories: AccessoryItemData[],
    field: string,
    dir: string
  ): AccessoryItemData[] {
    switch (field) {
      case 'price':
        return dir === 'asc'
          ? accessories.sort((a, b) => (a.cost_fractional < b.cost_fractional ? -1 : 1))
          : accessories.sort((a, b) => (a.cost_fractional > b.cost_fractional ? -1 : 1));
      case 'name':
        return dir === 'asc'
          ? accessories.sort((a, b) => (a.name < b.name ? -1 : 1))
          : accessories.sort((a, b) => (a.name > b.name ? -1 : 1));
      default:
        return dir === 'asc'
          ? accessories.sort((a, b) => (a.cost_fractional < b.cost_fractional ? -1 : 1))
          : accessories.sort((a, b) => (a.cost_fractional > b.cost_fractional ? -1 : 1));
    }
  }

  private updateCategoryWithAll(categories_data: ShoppingCategoryData[]): void {
    this.categories = categories_data.map((asset: ShoppingCategoryData) => {
      asset.items.unshift({
        item: 'All',
        material_icon_class: 'all_inclusive',
        display_name: this.translation_svc.translate('all'),
        active: !!!asset.items.find((category: ShoppingCategoryItem) => category.active)
          ? true
          : false,
        other_group_items: []
      });
      return asset;
    });
  }

  private retrieveCartAccessories(res: ShoppingCartData): void {
    this.accessory_cart_items = res.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
    );

    this.skip_button =
      this.accessory_cart_items.length === 0 && this.accessories.length > 0 ? true : false;

    this.accessory_cart_items.forEach((item: ShoppingCartItem) => {
      this.shopping_items_price.shopping_cart_items.forEach((price: ShoppingCartItem) => {
        if (item.id == price.id) {
          price.quantity = item.quantity;
        }
      });
    });

    for (let i: number = 0; i < this.accessories.length; i++) {
      let found: boolean = false;
      let quantity: number = 0;
      for (let j = 0; j < this.accessory_cart_items.length; j++) {
        if (this.accessory_cart_items[j].id == this.accessories[i].id) {
          found = true;
          quantity = this.accessory_cart_items[j].quantity!;
          break;
        }
      }
      this.accessories[i].in_cart = found;
      this.accessories[i].quantity = quantity;
    }

    this.refresh_summary.next(this.cart_updated ? 'refresh_cart_list' : true);
    this.pinCartAccessories();
  }

  private pinCartAccessories(): void {
    if (this.accessories) this.accessories.sort((a, b) => (a.in_cart! > b.in_cart! ? -1 : 1));
  }

  private setDefaultSort(): void {
    this.current_sort = { field: 'price', dir: 'asc' };
    this.selected_sort_dropdown_value = this.sort_options[0];
  }
}
